// src/pages/Profile/ProfileContainer.jsx
import React from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Container, Box, Typography } from '@mui/material';
import { useGetProfile } from '../../hooks/Users/useGetProfile';
import LoadingPage from '../../components/global/LoadingPage';
import WrapperSection from '../../components/global/WrapperSection';
import ProfileCard from './ProfileCard';

const ProfileContainer = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';

    const { Profile: profileResponse, isLoading, error } = useGetProfile();

    if (isLoading) return <LoadingPage />;

    if (error || !profileResponse) {
        return (
            <Container maxWidth="md">
                <Box sx={{ mt: 6, textAlign: 'center' }}>
                    <Typography variant="body1" color="error">
                        {isArabic
                            ? 'تعذر تحميل بيانات الملف الشخصي'
                            : 'Failed to load profile data'}
                    </Typography>
                </Box>
            </Container>
        );
    }

    const data = profileResponse?.data || {};
    const rolesNames = JSON.parse(localStorage.getItem('roles_names') || '[]');
    const isSuperAdmin = rolesNames.includes('super_admin');

    const createdAtFormatted = data.created_at
        ? new Date(data.created_at).toLocaleDateString(
            isArabic ? 'ar-EG' : 'en-US',
            { year: 'numeric', month: 'short', day: 'numeric' }
        )
        : '-';

    const displayRoles =
        data.roles_names?.length
            ? data.roles_names
            : data.roles?.map((r) => r.name) || [];

    return (
        <Box
            sx={{
                py: 6,
                px: 2,
                background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, #020617, #111827)'
                    : 'linear-gradient(135deg, #EEF2FF, #E0F2FE)',
                minHeight: 'calc(100vh - 64px)',
            }}
        >
            <WrapperSection>
                <ProfileHeader isArabic={isArabic} />
                <ProfileCard
                    isArabic={isArabic}
                    navigate={navigate}
                    user={data}
                    createdAtFormatted={createdAtFormatted}
                    displayRoles={displayRoles}
                    isSuperAdmin={isSuperAdmin}
                />
            </WrapperSection>
        </Box>
    );
};

const ProfileHeader = ({ isArabic }) => (
    <Box sx={{ mb: 3 }}>
        <Typography
            variant="h4"
            fontWeight={700}
            textAlign={isArabic ? 'right' : 'left'}
        >
            {isArabic ? 'الملف الشخصي' : 'Profile'}
        </Typography>
        <Typography
            variant="body2"
            color="text.secondary"
            textAlign={isArabic ? 'right' : 'left'}
        >
            {isArabic
                ? 'يمكنك هنا استعراض بيانات حسابك الأساسية.'
                : 'Here you can review your main account information.'}
        </Typography>
    </Box>
);

export default ProfileContainer;
